import React, { useState, useEffect, useRef } from 'react';
import { Card, Button } from 'components/ui';
import {
    DocumentTextIcon,
    ArrowDownTrayIcon,
    EyeIcon,
    EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import html2canvasPro from 'html2canvas-pro';
import { getInternationalStudentMasterFieldsWithOptions } from 'services/offerLetterService';
import { getInternationalOfferLetterByLeadId } from 'services/leadsService';
import EmailMessageModal from '../LeadActions/EmailMessageModal';
import { formatDisplayDate } from "../../../utils/dateUtils";
import { OfferLetterPDFTemplate } from './OfferLetterPDFTemplate';

/**
 * AcceptanceLetterTab Component
 * Dedicated tab for displaying and managing acceptance letters
 * Enabled/Shown only after offer letter is generated
 */
const AcceptanceLetterTab = ({
    leadId,
    leadData,
    isMobile = false,
    progressData,
}) => {
    const [downloading, setDownloading] = useState(false);
    const [viewing, setViewing] = useState(false);
    const [loading, setLoading] = useState(true);

    // Email modal states
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailAttachments, setEmailAttachments] = useState([]);
    const [preparingEmail, setPreparingEmail] = useState(false);

    // Dynamic offer letter / acceptance letter details states
    const [apiData, setApiData] = useState(null);
    const [formData, setFormData] = useState(null);
    const templateRef = useRef(null);

    const offerLetter = leadData?.offerLetter || null;
    const hasOfferLetter = !!(offerLetter && (offerLetter.FilePath || offerLetter.FileName || offerLetter.Id));

    // specific check for valid email to disable button if missing
    const leadEmail = leadData?.email || leadData?.Email || leadData?.EmailAddress;
    const hasValidEmail = leadEmail && leadEmail !== "N/A" && leadEmail.trim() !== "";

    useEffect(() => {
        const fetchDetails = async () => {
            if (!leadId) return;
            setLoading(true);
            try {
                // Fetch master fields with options
                let parsedFields = [];
                try {
                    const fieldsResponse = await getInternationalStudentMasterFieldsWithOptions();
                    if (fieldsResponse && (fieldsResponse.success || fieldsResponse.statusCode === 200)) {
                        const rawFields = fieldsResponse.data;
                        const parsed = typeof rawFields === 'string' ? JSON.parse(rawFields) : rawFields;
                        if (Array.isArray(parsed)) {
                            parsedFields = parsed;
                        }
                    }
                } catch (fErr) {
                    console.error("Error fetching master fields for acceptance letter:", fErr);
                }

                // Fetch offer letter record
                const response = await getInternationalOfferLetterByLeadId(leadId);
                if (response && (response.success || response.statusCode === 200)) {
                    const apiDataObj = response.data;

                    let rawJsonObj = {};
                    if (apiDataObj?.RawJson) {
                        rawJsonObj = typeof apiDataObj.RawJson === 'string' ? JSON.parse(apiDataObj.RawJson) : apiDataObj.RawJson;
                    }

                    const getFieldValueFromFields = (fields, fieldNameOrLabel) => {
                        if (!Array.isArray(fields)) return null;
                        const lower = fieldNameOrLabel.trim().toLowerCase();
                        const found = fields.find(f =>
                            f.FieldName?.trim().toLowerCase() === lower ||
                            f.FieldLabel?.trim().toLowerCase() === lower
                        );
                        return found ? found.Value : null;
                    };

                    const fields = apiDataObj?.OfferLetterFields || [];
                    const fName = getFieldValueFromFields(fields, 'first name') || getFieldValueFromFields(fields, 'first_name') || '';
                    const lName = getFieldValueFromFields(fields, 'last name') || getFieldValueFromFields(fields, 'last_name') || '';
                    const mergedName = [fName, lName].filter(Boolean).join(' ');

                    const initialData = {
                        applicantName: mergedName || leadData?.name || '',
                        applicantGender: getFieldValueFromFields(fields, 'gender') || '',
                        fatherGuardianName: getFieldValueFromFields(fields, "father's name") || getFieldValueFromFields(fields, "guardian's name") || '',
                        dateOfBirth: getFieldValueFromFields(fields, 'date_of_birth') || '',
                        applicantCountry: getFieldValueFromFields(fields, 'country') || '',
                        applicantPassportNo: getFieldValueFromFields(fields, 'passport no.') || '',
                        applicantSiiId: getFieldValueFromFields(fields, ' SII ID') || getFieldValueFromFields(fields, 'SII ID') || '',
                        applicantEmail: getFieldValueFromFields(fields, 'email') || leadData?.email || '',
                        fatherEmail: getFieldValueFromFields(fields, 'email address') || '',
                        fatherMobileNumber: getFieldValueFromFields(fields, "father's contact number") || '',
                        guardianEmail: '',
                        guardianMobileNumber: getFieldValueFromFields(fields, 'emergency contact details') || '',

                        collegeId: rawJsonObj.collegeId || '',
                        courseCode: rawJsonObj.courseCode || '',
                        programName: rawJsonObj.programName || '',
                        durationOfProgram: rawJsonObj.durationOfProgram || '',
                        startDate: rawJsonObj.startDate || '',
                        expectedEndDate: rawJsonObj.expectedEndDate || '',
                        semesters: rawJsonObj.semesters || '',
                        annualCourseFee: rawJsonObj.annualCourseFee || '',
                        scholarship: rawJsonObj.ScholarshipPercentage || '',
                        feeAfterScholarship: rawJsonObj.feeAfterScholarship || '',
                        provisionalRegistrationFeeDate: rawJsonObj.provisionalRegistrationFeeDate || '',
                    };

                    if (Array.isArray(parsedFields)) {
                        parsedFields.forEach((field) => {
                            const val = getFieldValueFromFields(fields, field.FieldName) || '';
                            initialData[field.FieldName] = val;
                        });
                    }

                    // Clean up initialData standard fields if they are "N/A" or "NA"
                    Object.keys(initialData).forEach(key => {
                        const val = initialData[key];
                        if (val && typeof val === 'string' && (val.trim().toLowerCase() === 'n/a' || val.trim().toLowerCase() === 'na')) {
                            initialData[key] = '';
                        }
                    });

                    setFormData(initialData);
                    setApiData(apiDataObj);
                }
            } catch (error) {
                console.error("Error loading saved international offer letter details for acceptance letter:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [leadId, leadData]);

    const generatePDF = async () => {
        if (!templateRef.current) {
            throw new Error("Template ref is not ready");
        }

        const pages = templateRef.current.children;
        if (!pages || pages.length === 0) {
            throw new Error("No pages found in template");
        }

        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: 'a4',
        });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        for (let i = 0; i < pages.length; i++) {
            const pageEl = pages[i];
            await new Promise((r) => setTimeout(r, 0));

            const canvas = await html2canvasPro(pageEl, {
                scale: 1.5, // Crisp rendering
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.92);

            if (i > 0) {
                pdf.addPage();
            }

            pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
        }

        return pdf;
    };

    const handleDownload = async () => {
        if (!formData) {
            toast.error('Acceptance letter data is not loaded yet');
            return;
        }

        setDownloading(true);
        const toastId = toast.loading('Generating Acceptance Letter PDF...');
        try {
            const pdf = await generatePDF();
            const fileName = `AcceptanceLetter_${(formData.applicantName || 'Student').trim().replace(/\s+/g, '_')}.pdf`;
            pdf.save(fileName);
            toast.success('Acceptance letter downloaded successfully', { id: toastId });
        } catch (error) {
            console.error('Error generating/downloading acceptance letter:', error);
            toast.error('Failed to generate/download acceptance letter', { id: toastId });
        } finally {
            setDownloading(false);
        }
    };

    const handleView = async () => {
        if (!formData) {
            toast.error('Acceptance letter data is not loaded yet');
            return;
        }

        setViewing(true);
        const toastId = toast.loading('Opening Acceptance Letter preview...');
        try {
            const pdf = await generatePDF();
            const pdfBlob = pdf.output('blob');
            const fileURL = URL.createObjectURL(pdfBlob);
            window.open(fileURL, '_blank');
            toast.success('Acceptance letter opened successfully', { id: toastId });
        } catch (error) {
            console.error('Error viewing acceptance letter:', error);
            toast.error('Failed to view acceptance letter', { id: toastId });
        } finally {
            setViewing(false);
        }
    };

    const handleSendEmail = async () => {
        if (!hasValidEmail) {
            toast.error("Lead has no email address");
            return;
        }

        setPreparingEmail(true);
        const toastId = toast.loading('Preparing Acceptance Letter for email...');
        try {
            const pdf = await generatePDF();
            const pdfBlob = pdf.output('blob');
            const fileName = `AcceptanceLetter_${(formData.applicantName || 'Student').trim().replace(/\s+/g, '_')}.pdf`;

            // Create a File object
            const file = new File([pdfBlob], fileName, {
                type: 'application/pdf'
            });

            // Set attachments and open modal
            setEmailAttachments([file]);
            setShowEmailModal(true);
            toast.success('Acceptance letter attached successfully', { id: toastId });
        } catch (error) {
            console.error('Error preparing email attachment:', error);
            toast.error('Failed to prepare acceptance letter for email', { id: toastId });
        } finally {
            setPreparingEmail(false);
        }
    };

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="text-center">
                    <div className="border-primary-600 mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2"></div>
                    <p className="dark:text-dark-400 text-gray-500">Loading Acceptance Letter...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <Card className="h-full border-none shadow-none p-0">
                {/* Header Section */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h4 className="text-lg font-bold text-gray-800 dark:text-white mb-1">
                            Acceptance Letter
                        </h4>
                        <p className="text-gray-500 text-sm">
                            Manage and track candidate acceptance details
                        </p>
                    </div>
                </div>

                {hasOfferLetter && formData ? (
                    <div className="space-y-6 animate-fade-in">
                        {/* Status Banner */}
                        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg p-4">
                            <div className="flex items-start">
                                <DocumentTextIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400 mt-1" />
                                <div className="ml-3 flex-1">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                        <div>
                                            <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                                                Acceptance Letter Available
                                            </h3>
                                            <div className="mt-1 space-y-1">
                                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                                    <span className="font-medium">Date:</span>
                                                    {formatDisplayDate(offerLetter.CreatedAt)}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                                    <span className="font-medium">Candidate:</span> {leadData?.name || 'N/A'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                size="md"
                                                onClick={handleView}
                                                disabled={viewing}
                                                className="flex-1 sm:flex-none justify-center"
                                            >
                                                <EyeIcon className="h-5 w-5 mr-2" />
                                                {viewing ? 'Opening...' : 'View'}
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                size="md"
                                                onClick={handleSendEmail}
                                                disabled={preparingEmail || !hasValidEmail || leadData?.IsStudent}
                                                title={leadData?.IsStudent ? "Option disabled for students" : (!hasValidEmail ? "Email address not available for this lead" : "Send acceptance letter via email")}
                                                className={`flex-1 sm:flex-none justify-center ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                                            >
                                                <EnvelopeIcon className="h-5 w-5 mr-2" />
                                                {preparingEmail ? 'Preparing...' : (!hasValidEmail ? 'No Email' : 'Email')}
                                            </Button>

                                            <Button
                                                variant="solid"
                                                color="primary"
                                                size="md"
                                                onClick={handleDownload}
                                                disabled={downloading}
                                                className="flex-1 sm:flex-none justify-center"
                                            >
                                                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                                                {downloading ? 'Downloading...' : 'Download'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="bg-gray-50 dark:bg-dark-800 rounded-lg p-4">
                            <h5 className="text-sm font-medium text-gray-900 dark:text-dark-100 mb-2">
                                About this acceptance letter
                            </h5>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                This acceptance letter is ready. You can view it, download it to your device, or email it directly to the candidate using the buttons above.                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-16 bg-gray-50 dark:bg-dark-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-dark-600">
                        <div className="flex justify-center mb-4">
                            <div className="p-4 bg-gray-200 dark:bg-dark-700 rounded-full">
                                <DocumentTextIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                            </div>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Acceptance Letter Not Available
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                            The acceptance letter will become available once the Offer Letter has been successfully generated for this international student.
                        </p>
                    </div>
                )}
            </Card>

            {/* Hidden layout for PDF generation */}
            {formData && (
                <div
                    style={{
                        position: 'fixed',
                        left: '-9999px',
                        top: '0',
                        transform: 'none',
                        opacity: 1,
                        visibility: 'visible',
                        zIndex: -9999
                    }}
                >
                    <OfferLetterPDFTemplate
                        ref={templateRef}
                        formData={formData}
                        leadData={leadData}
                        apiData={apiData}
                        letterType="acceptance"
                    />
                </div>
            )}

            {/* Email Modal */}
            <EmailMessageModal
                isOpen={showEmailModal}
                onClose={() => {
                    setShowEmailModal(false);
                    setEmailAttachments([]); // Clear attachments on close
                }}
                leadData={leadData}
                initialAttachments={emailAttachments}
            />
        </>
    );
};

export default AcceptanceLetterTab;
